import "./NewsListPage.scss";
import {
  AriaStatus,
  HeroBasic,
  LoadingOverlay,
  NewsListItemGroup,
  Pagination,
  ToastMessage,
} from "/App.Components";
import useListPageFilters, {
  BaseListFilters,
} from "/Helpers/Hooks/UseListPageFilters";
import { NewsListPageProps } from "/Models/Generated/NewsListPageProps";
import NewsListClient, { NewsListResults } from "./NewsListClient";
import { NewsListSectionProps } from "/Models/Generated/NewsListSectionProps";
import { useEffect, useRef, useState } from "react";
import useScrollTopTracker from "/Helpers/Hooks/useScrollTopTracker";
import useAriaUpdateHook from "/Helpers/Hooks/useAriaUpdateHook";

export interface NewsListFilters extends BaseListFilters {
  pageNumber: number;
}

const NewsListPage = ({
  pageHeader,
  newsList,
  endpoint,
  language,
  pagination,
  baseUrl,
}: NewsListPageProps) => {
  const client = new NewsListClient(
    endpoint,
    language,
    newsList?.newsListPageId,
    baseUrl,
  );
  const {
    items,
    resultsCount,
    error,
    loading,
    filters,
    totalPages,
    setError,
    setPageNumber,
  } = useListPageFilters({
    client: client,
    initList: newsList as NewsListResults,
    initFilters: {
      pageNumber: newsList?.currentPageNumber,
    },
  });

  const [doScroll, setDoScroll] = useState(false);
  const scrollToRef = useRef<HTMLDivElement>(null);
  const scrollTop = useScrollTopTracker(scrollToRef);

  useEffect(() => {
    if (loading) return;
    if (!doScroll) return;
    setDoScroll(false);
    window.scrollTo({
      top: scrollTop,
      behavior: "smooth",
    });
  }, [loading]);

  const { ariaStatus } = useAriaUpdateHook(resultsCount);

  return (
    <div className="news-list-page-norad">
      <ToastMessage
        message={error ?? ""}
        visible={!!error}
        onClose={() => setError("")}
      />
      <LoadingOverlay loading={loading} />
      {pageHeader && <HeroBasic {...pageHeader} />}
      <div className="news-list-page-norad__content" ref={scrollToRef}>
        <AriaStatus message={ariaStatus} />
        {items && (
          <ol className="news-list-page-norad__list">
            {(items as NewsListSectionProps[]).map((item, idx) => (
              <NewsListItemGroup key={"item-group" + idx} {...item} />
            ))}
          </ol>
        )}
        {items && (
          <div className="news-list-page-norad__pagination">
            <Pagination
              {...pagination}
              currentPage={filters.pageNumber ?? 1}
              setCurrentPage={(pageNum: number) => {
                setPageNumber(pageNum);
                setDoScroll(true);
              }}
              totalPages={totalPages ?? 1}
              nextHrefForCrawlers={
                (filters.pageNumber ?? totalPages ?? 1) < (totalPages ?? 1)
                  ? client.BuildUrlFromProps({
                      pageNumber: (filters.pageNumber ?? 1) + 1,
                    })
                  : undefined
              }
              previousHrefForCrawlers={
                (filters.pageNumber ?? 1) > 1
                  ? client.BuildUrlFromProps({
                      pageNumber: (filters.pageNumber ?? 1) - 1,
                    })
                  : undefined
              }
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default NewsListPage;
